import { z } from "zod";
import type { CarrierInstructionCostCandidate } from "~/shared/documents/carrier-instruction-costs";
import type { OrderDocumentAddressCandidate, OrderDocumentSavedFacts } from "~/shared/documents/order-document-source";

import { type CarrierContactPrefill, emptyCarrierContactPrefill } from "~/shared/documents/schemas/carrier-contact-prefill.schema";

/** Stable wire keys — UI labels come from the catalog, not attribute names. @see docs/OPT_IN_ORDER_DOCUMENTS.md */
export const DOCUMENT_ATTRIBUTE_WIRE_KEYS = {
  borderUs: {
    lineCountryOfManufacture: "border_us.line.countryOfManufacture",
    lineHsNumber: "border_us.line.hsNumber",
  },
  borderCanada: {
    lineTariffNumber: "border_canada.line.tariffNumber",
    lineDuty: "border_canada.line.duty",
  },
  bol: {
    lineCountryOfOrigin: "bol.line.countryOfOrigin",
    lineErgNumber: "bol.line.ergNumber",
    lineProducer: "bol.line.producer",
  },
} as const;

export type DocumentAttributeWireKey =
  | (typeof DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderUs)[keyof typeof DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderUs]
  | (typeof DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderCanada)[keyof typeof DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderCanada]
  | (typeof DOCUMENT_ATTRIBUTE_WIRE_KEYS.bol)[keyof typeof DOCUMENT_ATTRIBUTE_WIRE_KEYS.bol];

export type DocumentAttributeWireCandidate = {
  attributeId: string;
  attributeName: string;
  value: string;
};

export const DocumentAttributeWireCandidateSchema = z.object({
  attributeId: z.string().uuid(),
  attributeName: z.string(),
  value: z.string(),
});

/** Create-modal candidate maps. Opt-in slugs wire line fields only today — `headers` is always `{}`. */
export type DocumentAttributePrefillCandidates = {
  headers: Partial<Record<string, DocumentAttributeWireCandidate[]>>;
  lines: Partial<Record<string, Partial<Record<string, DocumentAttributeWireCandidate[]>>>>;
};

export const DocumentAttributePrefillCandidatesSchema = z.object({
  headers: z.record(z.array(DocumentAttributeWireCandidateSchema)).default({}),
  lines: z.record(z.record(z.array(DocumentAttributeWireCandidateSchema))).default({}),
});

export const emptyDocumentAttributePrefillCandidates = (): DocumentAttributePrefillCandidates => ({
  headers: {},
  lines: {},
});

/** Normalize legacy single-attribute values to ordered uuid arrays. */
export const normalizeDocumentAttributeWiring = (raw: unknown): Record<string, string[]> => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const normalized: Record<string, string[]> = {};
  for (const [wireKey, value] of Object.entries(raw)) {
    if (value == null) {
      continue;
    }
    if (typeof value === "string") {
      normalized[wireKey] = [value];
      continue;
    }
    if (Array.isArray(value)) {
      const attributeIds = value.filter((entry): entry is string => typeof entry === "string" && entry.length > 0);
      if (attributeIds.length > 0) {
        normalized[wireKey] = attributeIds;
      }
    }
  }
  return normalized;
};

export const DocumentAttributeWiringSchema = z.preprocess(
  normalizeDocumentAttributeWiring,
  z.record(z.array(z.string().uuid())).default({}),
);

export type DocumentAttributeWiring = z.infer<typeof DocumentAttributeWiringSchema>;

export type DocumentAttributeWireCatalogEntry = {
  wireKey: DocumentAttributeWireKey;
  label: string;
};

/** Update a wiring map by literal wire key (keys contain dots — not Mantine nested paths). */
export const mergeDocumentAttributeWiring = (
  current: Record<string, string | string[] | null | undefined> | undefined,
  wireKey: string,
  attributeIds: string[],
): Record<string, string[]> => {
  const next = normalizeDocumentAttributeWiring(current);
  if (attributeIds.length === 0) {
    delete next[wireKey];
    return next;
  }
  next[wireKey] = attributeIds;
  return next;
};

/** Line wire keys used by border-us prefill + create modal (keep in sync with catalog). */
export const BORDER_US_LINE_WIRE_KEYS = [
  DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderUs.lineCountryOfManufacture,
  DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderUs.lineHsNumber,
] as const;

export const BORDER_US_ATTRIBUTE_WIRE_CATALOG: DocumentAttributeWireCatalogEntry[] = [
  {
    wireKey: DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderUs.lineCountryOfManufacture,
    label: "Country of mfg",
  },
  {
    wireKey: DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderUs.lineHsNumber,
    label: "Line HS / tariff number",
  },
];

/** Order-level slots and legacy keys stripped on border-us config read. */
export const RETIRED_BORDER_US_ATTRIBUTE_WIRE_KEYS = [
  "border_us.tscaCertification",
  "border_us.countryOfOrigin",
  "border_us.countryOfManufacture",
  "border_us.line.tariffTreatment",
] as const;

const LEGACY_BORDER_US_HEADER_MANUFACTURE_WIRE_KEY = "border_us.countryOfManufacture";

/** Migrate legacy header wiring keys to current line-level slots. */
export const migrateBorderUsAttributeWiring = (wiring: DocumentAttributeWiring): DocumentAttributeWiring => {
  const next = { ...wiring };
  const legacyManufacture = next[LEGACY_BORDER_US_HEADER_MANUFACTURE_WIRE_KEY];
  if (legacyManufacture) {
    const lineKey = DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderUs.lineCountryOfManufacture;
    const merged = [...(next[lineKey] ?? []), ...legacyManufacture.filter(id => !(next[lineKey] ?? []).includes(id))];
    if (merged.length > 0) {
      next[lineKey] = merged;
    }
    delete next[LEGACY_BORDER_US_HEADER_MANUFACTURE_WIRE_KEY];
  }
  for (const wireKey of RETIRED_BORDER_US_ATTRIBUTE_WIRE_KEYS) {
    delete next[wireKey];
  }
  return next;
};

export const BORDER_CANADA_LINE_WIRE_KEYS = [
  DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderCanada.lineTariffNumber,
  DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderCanada.lineDuty,
] as const;

export const BORDER_CANADA_ATTRIBUTE_WIRE_CATALOG: DocumentAttributeWireCatalogEntry[] = [
  {
    wireKey: DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderCanada.lineTariffNumber,
    label: "Line tariff number",
  },
  {
    wireKey: DOCUMENT_ATTRIBUTE_WIRE_KEYS.borderCanada.lineDuty,
    label: "Line duty",
  },
];

/** Order-level slots removed from wiring — country of origin is manual on the create modal. */
export const RETIRED_BORDER_CANADA_ATTRIBUTE_WIRE_KEYS = ["border_canada.countryOfOrigin"] as const;

export const migrateBorderCanadaAttributeWiring = (wiring: DocumentAttributeWiring): DocumentAttributeWiring => {
  const next = { ...wiring };
  for (const wireKey of RETIRED_BORDER_CANADA_ATTRIBUTE_WIRE_KEYS) {
    delete next[wireKey];
  }
  return next;
};

export const BOL_LINE_WIRE_KEYS = [
  DOCUMENT_ATTRIBUTE_WIRE_KEYS.bol.lineCountryOfOrigin,
  DOCUMENT_ATTRIBUTE_WIRE_KEYS.bol.lineErgNumber,
  DOCUMENT_ATTRIBUTE_WIRE_KEYS.bol.lineProducer,
] as const;

export const BOL_ATTRIBUTE_WIRE_CATALOG: DocumentAttributeWireCatalogEntry[] = [
  {
    wireKey: DOCUMENT_ATTRIBUTE_WIRE_KEYS.bol.lineCountryOfOrigin,
    label: "Country of mfg (line suffix)",
  },
  {
    wireKey: DOCUMENT_ATTRIBUTE_WIRE_KEYS.bol.lineErgNumber,
    label: "ERG number (line suffix)",
  },
  {
    wireKey: DOCUMENT_ATTRIBUTE_WIRE_KEYS.bol.lineProducer,
    label: "Producer (line suffix)",
  },
];

export type OptInOrderDocumentPrefillResult<TInput> = {
  input: TInput;
  rateCostCandidates?: CarrierInstructionCostCandidate[];
  addressCandidates?: OrderDocumentAddressCandidate[];
  savedFacts?: OrderDocumentSavedFacts;
  /** Non-blocking customs FX notices (e.g. inverse rate derived from order freeze). */
  customsFxWarnings?: string[];
  attributePrefillCandidates: DocumentAttributePrefillCandidates;
  carrierContactPrefill: CarrierContactPrefill;
};

export const wrapOrderDocumentPrefill = <TInput>(
  input: TInput,
  attributePrefillCandidates: DocumentAttributePrefillCandidates = emptyDocumentAttributePrefillCandidates(),
  carrierContactPrefill: CarrierContactPrefill = emptyCarrierContactPrefill(),
  customsFxWarnings?: string[],
): OptInOrderDocumentPrefillResult<TInput> => ({
  input,
  attributePrefillCandidates,
  carrierContactPrefill,
  ...(customsFxWarnings?.length ? { customsFxWarnings } : {}),
});
